import type { ReactNode } from 'react';
import { Navigate, type RouteObject, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts';
import {
    Admin,
    CashFlow,
    GestaoUsuarios,
    Graficos,
    Help,
    Home,
    Importacoes,
    Lancamentos,
    LancarDespesa,
    LancarReceita,
    Login,
    PlanoContas,
    Register,
    Reports,
    ResetPassword,
    SelectFilial,
    Settings,
} from '../pages';
import {
    AdminCustomerDetail,
    AdminCustomers,
    AdminDashboard,
    AdminOnboardingSurvey,
    AdminPlans,
    AdminSettings,
    AdminSubscriptions,
    AdminTracking,
    AdminTriggers,
} from '../pages/Admin';
import { SuspendedScreen } from '../pages/SuspendedScreen/SuspendedScreen';
import { PlanFeature } from '../types';


interface ProtectedRouteProps {
    children: ReactNode;
}

/**
 * Rota que so existe dentro de um negocio especifico. Com "Todos os negocios"
 * ativo nao ha filial de destino, entao a tela volta para a home em vez de
 * lancar na primeira filial do usuario.
 */
function FilialEspecificaRoute({ children }: { children: ReactNode }) {
    const { filialAtiva } = useAuth();

    if (filialAtiva?.id === 'all') {
        return <Navigate to="/home" replace />;
    }

    return <>{children}</>;
}

interface FeatureRouteProps {
    children: ReactNode;
    feature: PlanFeature;
}

function FeatureRoute({ children, feature }: FeatureRouteProps) {
    const { isAuthenticated, hasFeature } = useAuth();

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (!hasFeature(feature)) {
        return <Navigate to="/home" replace />;
    }

    return <>{children}</>;
}

const ADMIN_ALLOWED_NON_ADMIN_ROUTES = ['/help', '/settings', '/select-filial'];

function ProtectedRoute({ children }: ProtectedRouteProps) {
    const { isAuthenticated, isAdmin, isDemo, isDemoRoute, user } = useAuth();
    const location = useLocation();

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Conta suspensa por inadimplência — mostra overlay de pagamento
    if (user?.status === 'suspended') {
        return <SuspendedScreen />;
    }

    // Admin pode acessar /help e /settings normalmente
    if (
        isAdmin &&
        !location.pathname.startsWith('/admin') &&
        !ADMIN_ALLOWED_NON_ADMIN_ROUTES.includes(location.pathname)
    ) {
        return <Navigate to="/admin" replace />;
    }

    if (isDemo && !isDemoRoute(location.pathname)) {
        return <Navigate to="/home" replace />;
    }

    return <>{children}</>;
}

interface PublicRouteProps {
    children: ReactNode;
}

function PublicRoute({ children }: PublicRouteProps) {
    const { isAuthenticated, isAdmin } = useAuth();

    if (isAuthenticated) {
        return <Navigate to={isAdmin ? '/admin' : '/home'} replace />;
    }

    return <>{children}</>;
}

interface AdminRouteProps {
    children: ReactNode;
}

interface GerenteRouteProps {
    children: ReactNode;
}

function GerenteRoute({ children }: GerenteRouteProps) {
    const { isAuthenticated, isGerente } = useAuth();

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (!isGerente) {
        return <Navigate to="/home" replace />;
    }

    return <>{children}</>;
}

function AdminRoute({ children }: AdminRouteProps) {
    const { isAuthenticated, isAdmin } = useAuth();

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (!isAdmin) {
        return <Navigate to="/home" replace />;
    }

    return <>{children}</>;
}

export const routes: RouteObject[] = [
    {
        path: '/',
        element: <Navigate to="/home" replace />,
    },
    {
        path: '/home',
        element: (
            <ProtectedRoute>
                <Home />
            </ProtectedRoute>
        ),
    },
    {
        path: '/login',
        element: (
            <PublicRoute>
                <Login />
            </PublicRoute>
        ),
    },
    {
        path: '/register',
        element: (
            <PublicRoute>
                <Register />
            </PublicRoute>
        ),
    },
    {
        path: '/reset-password',
        element: (
            <PublicRoute>
                <ResetPassword />
            </PublicRoute>
        ),
    },
    {
    path: '/graficos',
        element: (
            <ProtectedRoute>
                <Graficos />
            </ProtectedRoute>
        ),
    },

    {
        path: '/cashflow',
        element: (
            <ProtectedRoute>
                <CashFlow />
            </ProtectedRoute>
        ),
    },
    {
        path: '/reports',
        element: (
            <ProtectedRoute>
                <Reports />
            </ProtectedRoute>
        ),
    },
    {
        path: '/settings',
        element: (
            <ProtectedRoute>
                <Settings />
            </ProtectedRoute>
        ),
    },
    {
        path: '/importacoes',
        element: (
            <ProtectedRoute>
                <FeatureRoute feature="import">
                    <FilialEspecificaRoute>
                        <Importacoes />
                    </FilialEspecificaRoute>
                </FeatureRoute>
            </ProtectedRoute>
        ),
    },
    {
        path: '/select-filial',
        element: (
            <ProtectedRoute>
                <FeatureRoute feature="filiais">
                    <SelectFilial />
                </FeatureRoute>
            </ProtectedRoute>
        ),
    },
    {
        path: '/help',
        element: (
            <ProtectedRoute>
                <Help />
            </ProtectedRoute>
        ),
    },

    {
        path: '/lancamentos',
        element: (
            <ProtectedRoute>
                <Lancamentos />
            </ProtectedRoute>
        ),
    },
    {
        path: '/importar-lancamentos',
        element: <Navigate to="/home" replace />,
    },
    {
        path: '/lancar-receita',
        element: (
            <ProtectedRoute>
                <FilialEspecificaRoute>
                    <LancarReceita />
                </FilialEspecificaRoute>
            </ProtectedRoute>
        ),
    },
    {
        path: '/lancar-despesa',
        element: (
            <ProtectedRoute>
                <FilialEspecificaRoute>
                    <LancarDespesa />
                </FilialEspecificaRoute>
            </ProtectedRoute>
        ),
    },
    {
        path: '/plano-contas',
        element: (
            <ProtectedRoute>
                <PlanoContas />
            </ProtectedRoute>
        ),
    },
    {
        path: '/admin',
        element: (
            <AdminRoute>
                <AdminDashboard />
            </AdminRoute>
        ),
    },
    {
        path: '/admin/clientes',
        element: (
            <AdminRoute>
                <AdminCustomers />
            </AdminRoute>
        ),
    },
    {
        path: '/admin/clientes/:id',
        element: (
            <AdminRoute>
                <AdminCustomerDetail />
            </AdminRoute>
        ),
    },
    {
        path: '/admin/assinaturas',
        element: (
            <AdminRoute>
                <AdminSubscriptions />
            </AdminRoute>
        ),
    },
    {
        path: '/admin/planos',
        element: (
            <AdminRoute>
                <AdminPlans />
            </AdminRoute>
        ),
    },
    {
        path: '/admin/notificacoes',
        element: (
            <AdminRoute>
                <AdminTriggers />
            </AdminRoute>
        ),
    },
    {
        path: '/admin/tracking',
        element: (
            <AdminRoute>
                <AdminTracking />
            </AdminRoute>
        ),
    },
    {
        path: '/admin/pesquisa-onboarding',
        element: (
            <AdminRoute>
                <AdminOnboardingSurvey />
            </AdminRoute>
        ),
    },
    {
        path: '/admin/configuracoes',
        element: (
            <AdminRoute>
                <AdminSettings />
            </AdminRoute>
        ),
    },
    {
        path: '/admin/legacy',
        element: (
            <AdminRoute>
                <Admin />
            </AdminRoute>
        ),
    },
    {
        path: '/gestao-usuarios',
        element: (
            <GerenteRoute>
                <GestaoUsuarios />
            </GerenteRoute>
        ),
    },
    {
        path: '*',
        element: <Navigate to="/login" replace />,
    },
];
